/**
 * IntelliHealthRxBanner
 *
 * Workstream C — Inline Rx Safety Banner
 *
 * Displays at the top of PrescriptionFormSheet when prescribing.
 * Shows drug interactions, dose guidance, allergy alerts based on
 * current patient data.
 *
 * PRD Reference: Section 4C → Center Panel → Section 4 (Medications)
 */

import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Shield,
  Pill,
  Loader2,
  ChevronDown,
  ChevronUp,
  Info,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { patientService } from '@/services/patientService';

// ============================================================================
// Types
// ============================================================================

interface RxAlert {
  type: 'interaction' | 'allergy' | 'dose_guidance' | 'renal' | 'info';
  severity: 'critical' | 'warning' | 'info';
  message: string;
}

interface IntelliHealthRxBannerProps {
  patientId: string;
  className?: string;
}

// ============================================================================
// Severity styles
// ============================================================================

const SEVERITY_STYLES: Record<string, { bg: string; border: string; icon: React.ElementType; iconColor: string }> = {
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: AlertTriangle,
    iconColor: 'text-red-500',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: AlertTriangle,
    iconColor: 'text-amber-500',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: Info,
    iconColor: 'text-blue-500',
  },
};

// ============================================================================
// Component
// ============================================================================

const IntelliHealthRxBanner: React.FC<IntelliHealthRxBannerProps> = ({
  patientId,
  className,
}) => {
  const [alerts, setAlerts] = useState<RxAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadAlerts = async () => {
      if (!patientId) return;
      setIsLoading(true);

      try {
        // Fetch patient data to check for allergies and active medications
        const response = await patientService.getPatientById(patientId);
        if (cancelled) return;

        const patient = response?.data;
        if (!patient) {
          setAlerts([]);
          return;
        }

        const newAlerts: RxAlert[] = [];

        // Check allergies
        const allergies = patient.allergies || patient.medical_history?.allergies;
        if (allergies && typeof allergies === 'string' && allergies.trim() && allergies.toLowerCase() !== 'none' && allergies.toLowerCase() !== 'nkda') {
          newAlerts.push({
            type: 'allergy',
            severity: 'critical',
            message: `Known allergies: ${allergies}`,
          });
        } else if (Array.isArray(allergies) && allergies.length > 0) {
          newAlerts.push({
            type: 'allergy',
            severity: 'critical',
            message: `Known allergies: ${allergies.join(', ')}`,
          });
        }

        // Check active medications for potential interaction awareness
        const medications = patient.medications || patient.medical_history?.medications;
        if (medications) {
          const medList = typeof medications === 'string'
            ? medications.split(',').map((m: string) => m.trim()).filter(Boolean)
            : Array.isArray(medications) ? medications : [];

          if (medList.length > 0) {
            newAlerts.push({
              type: 'interaction',
              severity: 'warning',
              message: `Active medications (${medList.length}): ${medList.slice(0, 3).join(', ')}${medList.length > 3 ? '…' : ''}. Verify no interactions.`,
            });
          }
        }

        // Info about recent labs
        newAlerts.push({
          type: 'info',
          severity: 'info',
          message: 'Review recent labs before prescribing — check renal/hepatic function for dose adjustments.',
        });

        setAlerts(newAlerts);
      } catch (err) {
        console.error('[RxBanner] Failed to load patient safety data:', err);
        setAlerts([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadAlerts();
    return () => { cancelled = true; };
  }, [patientId]);

  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2', className)}>
        <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
        <span className="text-xs text-slate-500">Checking safety data…</span>
      </div>
    );
  }

  if (alerts.length === 0) return null;

  const highestSeverity = alerts.some((a) => a.severity === 'critical')
    ? 'critical'
    : alerts.some((a) => a.severity === 'warning')
      ? 'warning'
      : 'info';

  const styles = SEVERITY_STYLES[highestSeverity];
  const Icon = styles.icon;

  return (
    <div className={cn('rounded-lg border', styles.bg, styles.border, className)}>
      <button
        onClick={() => setIsExpanded((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2"
      >
        <div className="flex items-center gap-2">
          <Shield className="h-3.5 w-3.5 text-slate-400" />
          <Icon className={cn('h-4 w-4', styles.iconColor)} />
          <span className="text-xs font-medium text-slate-700">
            IntelliHealth Safety · {alerts.length} alert{alerts.length !== 1 ? 's' : ''}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-inherit px-3 pb-2.5 pt-1 space-y-1.5">
          {alerts.map((alert, i) => {
            const alertStyle = SEVERITY_STYLES[alert.severity];
            const AlertIcon = alertStyle.icon;
            return (
              <div key={i} className="flex items-start gap-2 text-xs">
                <AlertIcon className={cn('h-3.5 w-3.5 mt-0.5 flex-shrink-0', alertStyle.iconColor)} />
                <span className="text-slate-700">{alert.message}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IntelliHealthRxBanner;
